import type {
  Context,
  Emitter,
  Liquid,
  TagImpl,
  TagImplOptions,
  Template,
  TagToken,
  TopLevelToken,
} from "@hightouch/lib/templating/liquid";

import {
  HREF_SLOT,
  SRC_SLOT,
  TEXT_SLOT,
  translationRowId,
  type TranslationSlot,
} from "./elements";
import { sourceHash } from "./source-hash";
import {
  hasLocalizationTags,
  LANG_TAG,
  LOC_TAG,
  readTagArguments,
  TAG_ARGS,
  tagSourceText,
} from "./tags";
import {
  lookupTranslation,
  type TranslationAddress,
  type TranslationsForLocale,
} from "./translation-map";
import { findImageSrcHazard } from "./unsafe-html";

/**
 * `{% ht_loc <key> <slot> [off] %}` wraps its default content, so a miss renders the
 * source language rather than a blank — src included. Whether a locale may ship an
 * untranslated element is a question the render cannot answer: it cannot see which
 * locales a content block promised, and it cannot tell a preview from an email.
 * `assertTranslated` sees both and decides before any recipient is rendered.
 */

/** The key itself is an internal contract with the tag — set it via `renderCepTemplate`. */
export const TRANSLATIONS_CONTEXT_KEY = "__translations";

/**
 * Emits each text element inside `<span data-ht-key="<element key>:<slot>">`. The
 * attribute value is the same token the coverage rows carry, so the DOM and the
 * sidebar name a translatable unit identically and a click cannot resolve to
 * something the sidebar doesn't list. Never set on a path that delivers mail.
 */
export const LOCALIZATION_PREVIEW_CONTEXT_KEY = "__localizationPreview";

/**
 * The output is not HTML, so a translated `href` or `src` is written as the URL itself.
 * A push image URL goes to the gateway verbatim, where `&amp;` would break its query.
 */
export const LOCALIZATION_PLAIN_TEXT_CONTEXT_KEY = "__localizationPlainText";

/**
 * The language the copy renders in, for `{% ht_lang %}`.
 * The dispatcher sets the locale `translationsForRow` resolved, and a preview sets the locale it renders.
 */
export const LOCALE_CONTEXT_KEY = "__locale";

function getTranslations(ctx: Context): TranslationsForLocale | undefined {
  const map = ctx.get([TRANSLATIONS_CONTEXT_KEY]);
  return map instanceof Map ? (map as TranslationsForLocale) : undefined;
}

function isPreview(ctx: Context): boolean {
  return Boolean(ctx.get([LOCALIZATION_PREVIEW_CONTEXT_KEY]));
}

function isPlainText(ctx: Context): boolean {
  return Boolean(ctx.get([LOCALIZATION_PLAIN_TEXT_CONTEXT_KEY]));
}

/**
 * Both quoting styles, because the walk wraps `href='…'` as readily as `href="…"` and
 * an unescaped `'` would let a supplied translation introduce markup into a delivered
 * email. `&` goes first so it doesn't double-escape the entities that follow.
 */
function escapeAttributeValue(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

type TagIdentity = TranslationAddress & { off: boolean };

const LIQUID_DELIMITER_RE = /\{\{|\{%/;

function hasLiquid(value: string): boolean {
  return LIQUID_DELIMITER_RE.test(value);
}

/**
 * `assign`/`capture` write bindings that outlive the element they came from, so one
 * translation could rewrite the data every later element renders with. `include`/`render`
 * reach for the filesystem, which the engine's hooks refuse, and the refusal never
 * surfaces because the generator is stringified into the body instead of driven.
 */
const SCOPE_ESCAPING_TAG_RE = /\{%-?\s*(?:assign|capture|include|render)\b/;

/**
 * A translation is executable template code, and the CSV lane exists so the file can go
 * to someone who is not a workspace member and cannot already edit the body.
 */
export function isSafeToSubRender(value: string): boolean {
  // Re-entering this tag recurses until the stack is exhausted, and the RangeError
  // escapes every catch below: recovering from stack exhaustion needs stack.
  if (hasLocalizationTags(value)) return false;
  return !SCOPE_ESCAPING_TAG_RE.test(value);
}

/**
 * LiquidJS gives each tag occurrence its own impl object (`Object.create(impl)`), so
 * resolving the identity once at parse time is safe. The fields exist only after the
 * `parse` hook has run; rendering treats an absent one as "nothing to localize".
 */
interface LocTagImpl extends TagImpl {
  liquid: Liquid;
  /** Null when the body yielded no source text: the tag renders its default. */
  identity?: TagIdentity | null;
  templates?: Template[];
}

/**
 * Deliberately the SAME grammar (`TAG_ARGS`) and reader (`readTagArguments`) the
 * stamper and the element walk use, so a tag can never mean one thing to the CSV and
 * another to the render. Only the anchors are added here, since LiquidJS's `args` is
 * the argument list on its own rather than a slice of the document.
 */
const TAG_ARGS_RE = new RegExp(`^\\s*${TAG_ARGS}\\s*$`);

/**
 * The hash is taken over the raw source between the delimiters (token offsets), not
 * reassembled from parsed templates — reassembly would lose whitespace the normalizer
 * collapses, and the hash must match what `tagSourceText` sees in the stamped body.
 * Arguments that don't parse, or that name no valid slot, yield a null identity and
 * render the default: throwing at parse time would fail the whole body.
 */
function parseLocTag(
  this: LocTagImpl,
  token: TagToken,
  remainTokens: TopLevelToken[],
): void {
  const templates: Template[] = [];
  this.templates = templates;
  this.identity = null;
  const args = TAG_ARGS_RE.exec(token.args);
  const stream = this.liquid.parser.parseStream(remainTokens);
  stream
    .on(`tag:end${LOC_TAG}`, (endToken: TagToken) => {
      const parsed =
        args === null ? null : readTagArguments(args[1] ?? "", args[2] ?? "");
      const sourceText = tagSourceText(
        parsed?.slot ?? null,
        token.input.slice(token.end, endToken.begin),
        parsed?.elementKey,
      );
      if (parsed !== null && parsed.slot !== null && sourceText.length > 0) {
        this.identity = {
          elementKey: parsed.elementKey,
          slot: parsed.slot,
          off: parsed.off,
          sourceHash: sourceHash(sourceText),
        };
      }
      stream.stop();
    })
    .on("template", (tpl: Template) => templates.push(tpl))
    .on("end", () => {
      throw new Error(`tag ${token.getText()} not closed`);
    });
  stream.start();
}

/**
 * Undefined means "render the source language": the element is `off`, no row exists,
 * or a row was written against different text. The hash is verified here,
 * independently of whatever the stamper decided about identity, so a mis-carried key
 * can only ever cost a translation — never show wrong text.
 */
function lookup(
  ctx: Context,
  identity: TagIdentity | null | undefined,
): string | undefined {
  if (!identity || identity.off) return undefined;
  return lookupTranslation(getTranslations(ctx), identity);
}

/** Render the translation as its own sub-template, or the default on any error. */
function* resolve(
  impl: LocTagImpl,
  ctx: Context,
): Generator<unknown, { out: string; translated: boolean }, unknown> {
  const renderer = impl.liquid.renderer;
  const value = lookup(ctx, impl.identity);
  if (value !== undefined && isSafeToSubRender(value)) {
    if (!hasLiquid(value)) return { out: value, translated: true };
    // A translation with broken Liquid must not fail the body — at send time that
    // would cost a whole fragment of recipients their email. The upload ladder
    // rejects such values; this is the second line of defence.
    try {
      // Still executable code against the recipient's scope: a translation can emit any
      // profile column, not only the ones the author's own merge tags name.
      const rendered = (yield renderer.renderTemplates(
        impl.liquid.parse(value),
        ctx,
      )) as string;
      return { out: rendered, translated: true };
    } catch {
      // Fall through to the default.
    }
  }
  // Rendered only when it is the answer — this runs per element per recipient at send
  // time — and rendered rather than emitted raw, so an untranslated line still
  // resolves its merge tags and any attribute tag nested inside it.
  const fallback = (yield renderer.renderTemplates(
    impl.templates ?? [],
    ctx,
  )) as string;
  return { out: fallback, translated: false };
}

/** Schemes an email client or the preview iframe navigates rather than executes. */
const DELIVERABLE_HREF_PROTOCOLS = new Set([
  "https:",
  "http:",
  "mailto:",
  "tel:",
  "sms:",
]);

/**
 * Parsed rather than pattern-matched, so the scheme is read the way a browser reads it:
 * a tab inside `java\tscript:` or a leading NUL both still resolve to `javascript:`.
 */
function isDeliverableHref(value: string): boolean {
  try {
    // A relative href carries no scheme of its own and inherits the base's, which is
    // what lets `/sale`, `#sale` and a bare `sale/winter` through.
    const { protocol } = new URL(value, "https://relative.invalid");
    return DELIVERABLE_HREF_PROTOCOLS.has(protocol);
  } catch {
    // Unparseable is not deliverable.
    return false;
  }
}

function isDeliverableSrc(value: string): boolean {
  return findImageSrcHazard(value) === null;
}

/**
 * `text` emits raw — a translation legitimately carries mid-line markup, and escaping
 * it would show the tags to the recipient. `href`/`src`/`alt` land inside an attribute
 * value, so a translation is attribute-escaped, unless the render's output is plain
 * text (`LOCALIZATION_PLAIN_TEXT_CONTEXT_KEY`). The DEFAULT content is written back
 * verbatim in every slot: it is already escaped exactly as the source document had
 * it, and re-escaping would compound `&amp;` into `&amp;amp;` across saves.
 *
 * The `text` slot is unsanitized here by design, so the upload ladder's sanitizer is
 * load-bearing for the render and its gaps are this tag's gaps.
 */
function writeResolved(args: {
  slot: TranslationSlot;
  out: string;
  translated: boolean;
  plainText: boolean;
}): { value: string; undeliverable: boolean } {
  if (args.slot === TEXT_SLOT || !args.translated) {
    return { value: args.out, undeliverable: false };
  }
  // Empty rather than the source-language URL: a silently English link in a French
  // email is a correctness bug wearing a safety fix.
  if (args.slot === HREF_SLOT && !isDeliverableHref(args.out)) {
    return { value: "", undeliverable: true };
  }
  // Empty: `args.out` skipped `escapeAttributeValue`, so returning it would leave an
  // unescaped string one relaxed guard away from an attribute breakout.
  if (args.slot === SRC_SLOT && !isDeliverableSrc(args.out)) {
    return { value: "", undeliverable: true };
  }
  if (args.plainText) return { value: args.out, undeliverable: false };
  return { value: escapeAttributeValue(args.out), undeliverable: false };
}

interface LangTagImpl extends TagImpl {
  liquid: Liquid;
  templates?: Template[];
}

/** Arguments are ignored rather than refused: throwing at parse time would fail the whole body. */
function parseLangTag(
  this: LangTagImpl,
  token: TagToken,
  remainTokens: TopLevelToken[],
): void {
  const templates: Template[] = [];
  this.templates = templates;
  const stream = this.liquid.parser.parseStream(remainTokens);
  stream
    .on(`tag:end${LANG_TAG}`, () => stream.stop())
    .on("template", (tpl: Template) => templates.push(tpl))
    .on("end", () => {
      throw new Error(`tag ${token.getText()} not closed`);
    });
  stream.start();
}

function getLocale(ctx: Context): string | undefined {
  const locale = ctx.get([LOCALE_CONTEXT_KEY]);
  return typeof locale === "string" && locale.length > 0 ? locale : undefined;
}

export function registerLocalizationTags(engine: Liquid): void {
  const locTag: TagImplOptions = {
    parse: parseLocTag,
    *render(this: LocTagImpl, ctx: Context, emitter: Emitter) {
      // A translated line replaces its whole inner content, nested attribute tags
      // included — the URLs inside it come from the translation itself. A whole-line
      // link's href is stamped OUTSIDE this tag and keeps resolving independently.
      const { out, translated } = yield* resolve(this, ctx);
      const identity = this.identity;
      if (identity === null || identity === undefined) {
        // A malformed tag renders exactly what it wrapped, unescaped either way —
        // there is no slot to say which context it sits in.
        emitter.write(out);
        return;
      }
      const { value } = writeResolved({
        slot: identity.slot,
        out,
        translated,
        plainText: isPlainText(ctx),
      });
      emitter.write(
        isPreview(ctx) && identity.slot === TEXT_SLOT
          ? `<span data-ht-key="${translationRowId(identity)}">${value}</span>`
          : value,
      );
    },
  };

  engine.registerTag(LOC_TAG, locTag);

  const langTag: TagImplOptions = {
    parse: parseLangTag,
    *render(this: LangTagImpl, ctx: Context, emitter: Emitter) {
      const locale = getLocale(ctx);
      if (locale !== undefined) {
        emitter.write(escapeAttributeValue(locale));
        return;
      }
      // The authored value, written back as the source document had it, like an `ht_loc` default.
      const authored = (yield this.liquid.renderer.renderTemplates(
        this.templates ?? [],
        ctx,
      )) as string;
      emitter.write(authored);
    },
  };

  engine.registerTag(LANG_TAG, langTag);
}
